import { motion } from 'motion/react';
import { ChartNoAxesCombined, ShieldCheck, Sparkles } from 'lucide-react';
import { Logo } from '@/components/brand/logo';
import { AnimatedMark, TriangleMotif } from '@/components/brand/triangle-motif';
import { EASE_OUT } from '@/lib/motion';

const HIGHLIGHTS = [
  { icon: ChartNoAxesCombined, text: 'Pipeline, effort and revenue in one measured view' },
  { icon: Sparkles, text: 'A ready day plan for every business developer' },
  { icon: ShieldCheck, text: 'Compliance and audit built in from day one' },
];

export function BrandHero({ tenantName }: { tenantName?: string | undefined }) {
  return (
    <section
      aria-label="Ankyah CRM"
      className="relative hidden overflow-hidden bg-linear-to-br from-[#013f40] via-brand-strong to-brand text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16"
    >
      <div
        className="absolute -top-40 -right-40 size-[520px] rounded-full bg-accent/25 blur-3xl"
        aria-hidden
      />
      <div
        className="absolute -bottom-48 -left-24 size-[460px] rounded-full bg-[#00d2d1]/15 blur-3xl"
        aria-hidden
      />
      <TriangleMotif className="text-white/30" />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE_OUT }}
        className="relative"
      >
        <Logo onDark className="text-[21px]" />
      </motion.div>

      <div className="relative flex max-w-lg flex-col gap-8">
        <AnimatedMark className="size-28 drop-shadow-[0_12px_30px_rgba(0,0,0,0.25)]" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: EASE_OUT }}
        >
          {tenantName && (
            <p className="mb-3 text-sm font-medium tracking-wide text-[#9ff3f2]">Welcome, {tenantName}</p>
          )}
          <h2 className="font-display text-4xl leading-tight font-semibold xl:text-5xl">
            Business development,
            <br />
            <span className="bg-linear-to-r from-[#7ff5f4] to-white bg-clip-text text-transparent">
              measured.
            </span>
          </h2>
          <p className="mt-4 text-base text-white/75">
            The CRM that turns your playbook into a daily plan — and every touch into insight.
          </p>
        </motion.div>
        <ul className="flex flex-col gap-3">
          {HIGHLIGHTS.map(({ icon: Icon, text }, i) => (
            <motion.li
              key={text}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45 + i * 0.08, duration: 0.35, ease: EASE_OUT }}
              className="flex items-center gap-3 text-sm text-white/85"
            >
              <span className="flex size-8 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15 backdrop-blur">
                <Icon className="size-4" aria-hidden />
              </span>
              {text}
            </motion.li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs text-white/55">© {new Date().getFullYear()} Ankyah Nexus · CRM-Lite</p>
    </section>
  );
}
